import { getCachedEnvConfig, readCitizenDashboardUrl, readEmbedConfiguredUrls, readProcessFlowViewerOrigin, } from '../services/envConfig';
import { isEmbedUrlAllowedByEnv } from '../components/library/Utils/embedAllowedOrigins';
import { getStaticBaseUrl } from './publicUrl';
/** Search param on `/permits/:id` for in-page details iframe */
export const VIEW_DETAILS_QUERY = 'viewDetails';
/** Route slug for `/permits/:departmentId/details` when embedding portal apps (citizen dashboard, flow viewer, etc.). */
export const PORTAL_SHELL_DEPARTMENT_ID = 'portal';
function urlsEqualForRouting(a, b) {
    try {
        return new URL(a.trim()).href === new URL(b.trim()).href;
    }
    catch {
        return a.trim() === b.trim();
    }
}
/**
 * Dashboard menu links that would otherwise `window.location.assign` to an external http(s) app
 * should open inside {@link PORTAL_SHELL_DEPARTMENT_ID} details shell (NavBar + iframe) instead.
 */
export function shouldEmbedDashboardHrefInPortalShell(href, env) {
    const t = href.trim();
    if (!/^https?:\/\//i.test(t))
        return false;
    if (!isEmbedUrlAllowed(t, env))
        return false;
    if (isProcessFlowDetailsEmbedUrl(t))
        return true;
    const citizen = readCitizenDashboardUrl(env);
    if (citizen && urlsEqualForRouting(t, citizen))
        return true;
    const flowOrigin = readProcessFlowViewerOrigin(env);
    if (flowOrigin) {
        try {
            if (new URL(t).origin === new URL(flowOrigin).origin)
                return true;
        }
        catch {
            /* ignore */
        }
    }
    return false;
}
const REF_PREFIX = 'ref:';
const SS_PREFIX = 'permitDetailsEmbed:';
/** Stay under typical URL length limits; long URLs use sessionStorage indirection */
const MAX_ENCODED_LEN = 1800;
export function encodeDetailsForQuery(url) {
    const enc = encodeURIComponent(url);
    if (enc.length <= MAX_ENCODED_LEN)
        return enc;
    const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    try {
        sessionStorage.setItem(SS_PREFIX + id, url);
    }
    catch {
        return enc;
    }
    return `${REF_PREFIX}${id}`;
}
/**
 * Raw value for `URLSearchParams.set` / `navigate({ search })` so encoding happens **once**.
 * Passing `encodeDetailsForQuery()` into `createSearchParams()` double-encodes and breaks URLs with `#/...` (e.g. Process Flow).
 */
export function rawViewDetailsQueryValue(url) {
    const t = url.trim();
    const enc = encodeURIComponent(t);
    if (enc.length <= MAX_ENCODED_LEN)
        return t;
    const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    try {
        sessionStorage.setItem(SS_PREFIX + id, t);
    }
    catch {
        return t;
    }
    return `${REF_PREFIX}${id}`;
}
export function decodeDetailsFromQuery(value) {
    if (!value)
        return '';
    if (value.startsWith(REF_PREFIX)) {
        const id = value.slice(REF_PREFIX.length);
        return sessionStorage.getItem(SS_PREFIX + id) ?? '';
    }
    try {
        return decodeURIComponent(value);
    }
    catch {
        return '';
    }
}
/** Syntax only: any absolute http(s) URL. Not an allow-list; check {@link isEmbedUrlAllowed} before framing a URL. */
export function isHttpUrlForEmbed(url) {
    try {
        const u = new URL(url);
        return u.protocol === 'https:' || u.protocol === 'http:';
    }
    catch {
        return false;
    }
}
/**
 * True when `url` may be loaded into a portal iframe (SafeAeon v1 EXT-002): its origin is this page's, or one that
 * `env.json` allows: `EmbedAllowedOrigins`, or the app URLs from `readEmbedConfiguredUrls`. With no env loaded,
 * only same-origin URLs pass.
 * Check this before framing a URL or putting it in `?viewDetails=`.
 */
export function isEmbedUrlAllowed(url, env = getCachedEnvConfig()) {
    return isEmbedUrlAllowedByEnv(url, readEmbedConfiguredUrls(env));
}
/**
 * True for Flow Builder “process viewer” URLs (`…/ProcessFlows/…/…/…/view`).
 * These must open on the permit details page inside an iframe — not via HashRouter `navigate` on the same
 * origin (which would nest `#/ProcessFlows/…` under the CMS hash and break the embedded app).
 */
export function isProcessFlowDetailsEmbedUrl(url) {
    const t = url.trim();
    if (!isHttpUrlForEmbed(t))
        return false;
    return /#\/ProcessFlows\/|\/ProcessFlows\//i.test(t);
}
export function resolveToAbsoluteUrl(url) {
    const t = url.trim();
    if (!t)
        return '';
    if (/^https?:\/\//i.test(t))
        return t;
    if (t.startsWith('/'))
        return `${window.location.origin}${t}`;
    return t;
}
function normalizeDeployPathname(pathname) {
    let p = pathname.replace(/\/index\.html$/i, '');
    if (p === '' || p === '/')
        return '/';
    return p.replace(/\/$/, '') || '/';
}
/**
 * True when `absoluteUrl` targets this CMS viewer (same origin and under its static base path).
 * Other same-origin apps (e.g. /ONESTOP-FlowViewer/) must not use HashRouter `navigate` —
 * that nests their path in the viewer hash (e.g. #/ONESTOP-FlowViewer/#/ProcessFlows/...).
 */
export function isCmsViewerSameOriginUrl(absoluteUrl) {
    try {
        const u = new URL(absoluteUrl);
        if (u.origin !== window.location.origin)
            return false;
        const basePath = normalizeDeployPathname(new URL(getStaticBaseUrl()).pathname);
        const reqPath = normalizeDeployPathname(u.pathname);
        if (basePath === '/') {
            return (reqPath === '/' ||
                reqPath.startsWith('/permits') ||
                reqPath.startsWith('/licenses'));
        }
        return reqPath === basePath || reqPath.startsWith(`${basePath}/`);
    }
    catch {
        return false;
    }
}
